import { useState, useEffect } from 'react'
import {
  Box,
  Grid,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  IconButton,
  Chip,
  Tabs,
  Tab,
} from '@mui/material'
import { Add, Edit, Delete, PlayArrow } from '@mui/icons-material'
import { PageHeader, Button, DataTable, StatusChip, Card, Modal } from '@components/common'
import { toast } from 'react-toastify'
import api from '@services/api'

const FREQUENCY_OPTIONS = [
  { value: 'daily', label: 'Daily' },
  { value: 'weekly', label: 'Weekly' },
  { value: 'biweekly', label: 'Bi-Weekly' },
  { value: 'monthly', label: 'Monthly' },
  { value: 'quarterly', label: 'Quarterly' },
  { value: 'semi_annual', label: 'Semi-Annual' },
  { value: 'annual', label: 'Annual' },
]

const STATUS_OPTIONS = [
  { value: 'scheduled', label: 'Scheduled' },
  { value: 'in_progress', label: 'In Progress' },
  { value: 'completed', label: 'Completed' },
  { value: 'overdue', label: 'Overdue' },
  { value: 'skipped', label: 'Skipped' },
]

// PM Templates Tab
const PMTemplatesTab = () => {
  const [templates, setTemplates] = useState([])
  const [categories, setCategories] = useState([])
  const [loading, setLoading] = useState(true)
  const [formOpen, setFormOpen] = useState(false)
  const [selectedTemplate, setSelectedTemplate] = useState(null)
  const [saving, setSaving] = useState(false)
  const [formData, setFormData] = useState({
    name: '',
    category: '',
    frequency: 'monthly',
    frequency_days: 30,
    estimated_duration_hours: 1,
    description: '',
    is_active: true,
  })

  const fetchData = async () => {
    try {
      setLoading(true)
      const [templatesRes, categoriesRes] = await Promise.all([
        api.get('/maintenance/pm-templates/'),
        api.get('/assets/categories/'),
      ])
      setTemplates(templatesRes.data.results || templatesRes.data)
      setCategories(categoriesRes.data.results || categoriesRes.data)
    } catch (error) {
      console.error('Error fetching data:', error)
      toast.error('Failed to load PM templates')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchData()
  }, [])

  const handleAdd = () => {
    setSelectedTemplate(null)
    setFormData({
      name: '',
      category: '',
      frequency: 'monthly',
      frequency_days: 30,
      estimated_duration_hours: 1,
      description: '',
      is_active: true,
    })
    setFormOpen(true)
  }

  const handleEdit = (template) => {
    setSelectedTemplate(template)
    // Handle both nested objects and direct IDs
    const categoryId = template.category?.id || template.category || ''
    setFormData({
      name: template.name,
      category: categoryId,
      frequency: template.frequency,
      frequency_days: template.frequency_days,
      estimated_duration_hours: template.estimated_duration_hours,
      description: template.description || '',
      is_active: template.is_active,
    })
    setFormOpen(true)
  }

  const handleSave = async () => {
    setSaving(true)
    try {
      const payload = { ...formData, category: formData.category || null }
      if (selectedTemplate) {
        await api.patch(`/maintenance/pm-templates/${selectedTemplate.id}/`, payload)
        toast.success('Template updated successfully')
      } else {
        await api.post('/maintenance/pm-templates/', payload)
        toast.success('Template created successfully')
      }
      setFormOpen(false)
      fetchData()
    } catch (error) {
      console.error('Error saving template:', error)
      toast.error(error.response?.data?.error || 'Failed to save template')
    } finally {
      setSaving(false)
    }
  }

  const columns = [
    { field: 'name', headerName: 'Template Name', sortable: true },
    {
      field: 'category_name',
      headerName: 'Category',
      renderCell: (v, row) => v || row.category?.name || '-',
    },
    {
      field: 'frequency',
      headerName: 'Frequency',
      renderCell: (v) => <Chip label={v?.replace('_', ' ')} size="small" sx={{ textTransform: 'capitalize' }} />,
    },
    { field: 'frequency_days', headerName: 'Days' },
    { field: 'estimated_duration_hours', headerName: 'Est. Hours' },
    {
      field: 'is_active',
      headerName: 'Status',
      renderCell: (value) => <StatusChip status={value ? 'active' : 'inactive'} />,
    },
    {
      field: 'actions',
      headerName: '',
      renderCell: (_, row) => (
        <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleEdit(row) }}>
          <Edit fontSize="small" />
        </IconButton>
      ),
    },
  ]

  return (
    <>
      <Box display="flex" justifyContent="flex-end" mb={2}>
        <Button startIcon={<Add />} onClick={handleAdd}>Add Template</Button>
      </Box>
      <DataTable
        columns={columns}
        data={templates}
        loading={loading}
        searchPlaceholder="Search templates..."
        emptyMessage="No PM templates found"
      />

      <Modal
        open={formOpen}
        onClose={() => setFormOpen(false)}
        title={selectedTemplate ? 'Edit PM Template' : 'Add PM Template'}
        maxWidth="sm"
        actions={
          <>
            <Button variant="outlined" onClick={() => setFormOpen(false)}>Cancel</Button>
            <Button onClick={handleSave} loading={saving}>{selectedTemplate ? 'Update' : 'Create'}</Button>
          </>
        }
      >
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Template Name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              required
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth>
              <InputLabel>Category</InputLabel>
              <Select
                value={formData.category}
                onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                label="Category"
              >
                <MenuItem value="">All Categories</MenuItem>
                {categories.map((c) => (
                  <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth>
              <InputLabel>Frequency</InputLabel>
              <Select
                value={formData.frequency}
                onChange={(e) => setFormData({ ...formData, frequency: e.target.value })}
                label="Frequency"
              >
                {FREQUENCY_OPTIONS.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Frequency Days"
              type="number"
              value={formData.frequency_days}
              onChange={(e) => setFormData({ ...formData, frequency_days: parseInt(e.target.value) })}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Estimated Hours"
              type="number"
              value={formData.estimated_duration_hours}
              onChange={(e) => setFormData({ ...formData, estimated_duration_hours: parseFloat(e.target.value) })}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Description"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              multiline
              rows={2}
            />
          </Grid>
        </Grid>
      </Modal>
    </>
  )
}

// PM Schedules Tab
const PMSchedulesTab = () => {
  const [schedules, setSchedules] = useState([])
  const [assets, setAssets] = useState([])
  const [templates, setTemplates] = useState([])
  const [technicians, setTechnicians] = useState([])
  const [loading, setLoading] = useState(true)
  const [formOpen, setFormOpen] = useState(false)
  const [selectedSchedule, setSelectedSchedule] = useState(null)
  const [saving, setSaving] = useState(false)
  const [formData, setFormData] = useState({
    asset: '',
    template: '',
    scheduled_date: '',
    due_date: '',
    status: 'scheduled',
    assigned_to: '',
    notes: '',
  })

  const fetchData = async () => {
    try {
      setLoading(true)
      const [schedulesRes, assetsRes, templatesRes, techniciansRes] = await Promise.all([
        api.get('/maintenance/pm-schedules/'),
        api.get('/assets/assets/'),
        api.get('/maintenance/pm-templates/'),
        api.get('/common/profiles/technicians/').catch(() => ({ data: [] })),
      ])
      setSchedules(schedulesRes.data.results || schedulesRes.data)
      setAssets(assetsRes.data.results || assetsRes.data)
      setTemplates(templatesRes.data.results || templatesRes.data)
      // Technicians come from profiles endpoint
      const techData = techniciansRes.data.results || techniciansRes.data || []
      setTechnicians(techData)
    } catch (error) {
      console.error('Error fetching data:', error)
      toast.error('Failed to load PM schedules data')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchData()
  }, [])

  const handleAdd = () => {
    setSelectedSchedule(null)
    const today = new Date().toISOString().split('T')[0]
    setFormData({
      asset: '',
      template: '',
      scheduled_date: today,
      due_date: today,
      status: 'scheduled',
      assigned_to: '',
      notes: '',
    })
    setFormOpen(true)
  }

  const handleEdit = (schedule) => {
    setSelectedSchedule(schedule)
    // Handle both nested objects and direct IDs (list vs detail serializer)
    const assetId = schedule.asset_detail?.id || schedule.asset?.id || schedule.asset || ''
    const templateId = schedule.template_detail?.id || schedule.template?.id || schedule.template || ''
    const assignedToId = schedule.assigned_to_detail?.id || schedule.assigned_to?.id || schedule.assigned_to || ''

    setFormData({
      asset: assetId,
      template: templateId,
      scheduled_date: schedule.scheduled_date || '',
      due_date: schedule.due_date || '',
      status: schedule.status,
      assigned_to: assignedToId,
      notes: schedule.notes || '',
    })
    setFormOpen(true)
  }

  const handleSave = async () => {
    setSaving(true)
    try {
      const payload = {
        ...formData,
        asset: formData.asset || null,
        template: formData.template || null,
        assigned_to: formData.assigned_to || null,
      }
      if (selectedSchedule) {
        await api.patch(`/maintenance/pm-schedules/${selectedSchedule.id}/`, payload)
        toast.success('Schedule updated successfully')
      } else {
        await api.post('/maintenance/pm-schedules/', payload)
        toast.success('PM scheduled successfully')
      }
      setFormOpen(false)
      fetchData()
    } catch (error) {
      console.error('Error saving schedule:', error)
      toast.error(error.response?.data?.error || 'Failed to save schedule')
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async (schedule) => {
    if (!window.confirm(`Delete PM schedule for ${schedule.asset?.name}?`)) return
    try {
      await api.delete(`/maintenance/pm-schedules/${schedule.id}/`)
      toast.success('Schedule deleted')
      fetchData()
    } catch (error) {
      console.error('Error deleting schedule:', error)
      toast.error('Failed to delete schedule')
    }
  }

  const handleStartPM = async (schedule) => {
    try {
      await api.patch(`/maintenance/pm-schedules/${schedule.id}/`, {
        status: 'in_progress',
      })
      toast.success('PM started')
      fetchData()
    } catch (error) {
      console.error('Error starting PM:', error)
      toast.error('Failed to start PM')
    }
  }

  const columns = [
    {
      field: 'asset_name',
      headerName: 'Asset',
      sortable: true,
      renderCell: (v, row) => v || row.asset?.name || '-',
    },
    {
      field: 'template_name',
      headerName: 'PM Template',
      renderCell: (v, row) => v || row.template?.name || '-',
    },
    {
      field: 'scheduled_date',
      headerName: 'Scheduled',
      renderCell: (v) => v ? new Date(v).toLocaleDateString() : '-',
      sortable: true,
    },
    {
      field: 'due_date',
      headerName: 'Due Date',
      renderCell: (v) => v ? new Date(v).toLocaleDateString() : '-',
      sortable: true,
    },
    {
      field: 'assigned_to_name',
      headerName: 'Assigned To',
      renderCell: (v, row) => v || row.assigned_to?.username || 'Unassigned',
    },
    {
      field: 'status',
      headerName: 'Status',
      renderCell: (value) => <StatusChip status={value} />,
    },
    {
      field: 'actions',
      headerName: '',
      width: 150,
      renderCell: (_, row) => (
        <Box display="flex" gap={0.5}>
          {row.status === 'scheduled' && (
            <IconButton size="small" color="primary" onClick={(e) => { e.stopPropagation(); handleStartPM(row) }} title="Start PM">
              <PlayArrow fontSize="small" />
            </IconButton>
          )}
          <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleEdit(row) }}>
            <Edit fontSize="small" />
          </IconButton>
          <IconButton size="small" color="error" onClick={(e) => { e.stopPropagation(); handleDelete(row) }}>
            <Delete fontSize="small" />
          </IconButton>
        </Box>
      ),
    },
  ]

  return (
    <>
      <Box display="flex" justifyContent="flex-end" mb={2}>
        <Button startIcon={<Add />} onClick={handleAdd}>Schedule PM</Button>
      </Box>
      <DataTable
        columns={columns}
        data={schedules}
        loading={loading}
        searchPlaceholder="Search schedules..."
        emptyMessage="No PM schedules found"
      />

      <Modal
        open={formOpen}
        onClose={() => setFormOpen(false)}
        title={selectedSchedule ? 'Edit PM Schedule' : 'Schedule Preventive Maintenance'}
        maxWidth="sm"
        actions={
          <>
            <Button variant="outlined" onClick={() => setFormOpen(false)}>Cancel</Button>
            <Button onClick={handleSave} loading={saving}>{selectedSchedule ? 'Update' : 'Schedule'}</Button>
          </>
        }
      >
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <FormControl fullWidth required>
              <InputLabel>Asset</InputLabel>
              <Select
                value={formData.asset}
                onChange={(e) => setFormData({ ...formData, asset: e.target.value })}
                label="Asset"
              >
                {assets.map((a) => (
                  <MenuItem key={a.id} value={a.id}>{a.name} ({a.asset_tag})</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <FormControl fullWidth>
              <InputLabel>PM Template</InputLabel>
              <Select
                value={formData.template}
                onChange={(e) => setFormData({ ...formData, template: e.target.value })}
                label="PM Template"
              >
                <MenuItem value="">None</MenuItem>
                {templates.map((t) => (
                  <MenuItem key={t.id} value={t.id}>{t.name} ({t.frequency})</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Scheduled Date"
              type="date"
              value={formData.scheduled_date}
              onChange={(e) => setFormData({ ...formData, scheduled_date: e.target.value })}
              InputLabelProps={{ shrink: true }}
              required
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Due Date"
              type="date"
              value={formData.due_date}
              onChange={(e) => setFormData({ ...formData, due_date: e.target.value })}
              InputLabelProps={{ shrink: true }}
              required
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth>
              <InputLabel>Assigned To</InputLabel>
              <Select
                value={formData.assigned_to}
                onChange={(e) => setFormData({ ...formData, assigned_to: e.target.value })}
                label="Assigned To"
              >
                <MenuItem value="">Unassigned</MenuItem>
                {technicians.map((t) => (
                  <MenuItem key={t.id} value={t.user?.id || t.id}>
                    {t.full_name || t.user?.first_name + ' ' + t.user?.last_name || t.user?.username || 'Unknown'}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                value={formData.status}
                onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                label="Status"
              >
                {STATUS_OPTIONS.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Notes"
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              multiline
              rows={2}
            />
          </Grid>
        </Grid>
      </Modal>
    </>
  )
}

const PMScheduleList = () => {
  const [activeTab, setActiveTab] = useState(0)

  return (
    <Box>
      <PageHeader
        title="Preventive Maintenance"
        subtitle="Manage PM schedules and templates"
        breadcrumbs={[
          { label: 'Dashboard', path: '/dashboard' },
          { label: 'PM Schedule' },
        ]}
      />

      <Card>
        <Tabs
          value={activeTab}
          onChange={(_, newValue) => setActiveTab(newValue)}
          sx={{ mb: 3 }}
        >
          <Tab label="PM Schedules" />
          <Tab label="PM Templates" />
        </Tabs>

        {activeTab === 0 && <PMSchedulesTab />}
        {activeTab === 1 && <PMTemplatesTab />}
      </Card>
    </Box>
  )
}

export default PMScheduleList
